import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Sparkles, 
  ShieldCheck, 
  TrendingUp, 
  Bell, 
  ArrowRight,
  Upload,
  Video,
  Briefcase,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  Star,
  Award,
  Loader2
} from 'lucide-react';
import { candidate } from '../../api';

const EnhancedDashboard = () => {
  const navigate = useNavigate();
  const [aiSummary, setAiSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const userName = localStorage.getItem('userName') || 'User';
  
  const [stats, setStats] = useState({
    profileCompletion: 50,
    matchScore: 0,
    verifiedStatus: false,
    jobMatches: 0,
    interviewsScheduled: 0
  });
  
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');
    
    if (!token || userType !== 'candidate') {
      navigate('/login');
      return;
    }
    
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [profileData, statsData, notificationsData] = await Promise.all([
        candidate.getProfile().catch(() => null),
        candidate.getStats().catch(() => ({
          profile_completion: 50,
          job_matches: 0,
          interviews_scheduled: 0,
          ai_score: 0
        })),
        candidate.getNotifications().catch(() => [])
      ]);

      if (profileData) {
        setCandidateProfile(profileData);
        setStats({
          profileCompletion: statsData.profile_completion || 50,
          matchScore: statsData.ai_score || 75,
          verifiedStatus: profileData.status === 'verified',
          jobMatches: statsData.job_matches || 0,
          interviewsScheduled: statsData.interviews_scheduled || 0
        });
        
        // Generate AI summary based on profile
        const profession = profileData.profession || 'healthcare';
        const specialisation = profileData.specialisation || '';
        const videoStatus = profileData.video_status === 'completed';
        
        setTimeout(() => {
          let summary = `Your profile shows strong ${profession.toLowerCase()} experience`;
          if (specialisation) summary += ` with ${specialisation.toLowerCase()} specialisation`;
          if (profileData.country) summary += ` and verified ${profileData.country} credentials`;
          summary += `. You have ${statsData.job_matches || 0} potential matches.`;
          if (!videoStatus) {
            summary += ' Complete your video interview to unlock more opportunities.';
          }
          setAiSummary(summary);
        }, 1000);
      }

      // Format notifications
      setNotifications(notificationsData.slice(0, 3).map(notif => ({
        id: notif._id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        time: new Date(notif.created_at).toLocaleDateString(),
        unread: !notif.read,
        icon: notif.type === 'match' ? Sparkles : notif.type === 'interview' ? Calendar : CheckCircle2,
        color: notif.type === 'match' ? 'text-teal-600' : notif.type === 'interview' ? 'text-blue-600' : 'text-green-600'
      })));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSteps = [
    {
      id: 1,
      title: 'Complete Video Interview',
      description: 'Short video for initial screening support',
      priority: candidateProfile?.video_status !== 'completed' ? 'high' : 'normal',
      action: '/candidate/video-interview',
      icon: Video,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      progress: candidateProfile?.video_status === 'completed' ? 100 : 0
    },
    {
      id: 2,
      title: 'Complete Profile Review',
      description: 'Upload and organise your credentials',
      priority: candidateProfile?.document_status !== 'verified' ? 'medium' : 'normal',
      action: '/candidate/verification',
      icon: Upload,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      progress: candidateProfile?.documents?.length > 0 ? 60 : 0
    },
    {
      id: 3,
      title: 'Review Job Matches',
      description: `${stats.jobMatches} positions matched to your profile`,
      priority: 'normal',
      action: '/candidate/job-matches',
      icon: Briefcase,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      progress: 100
    }
  ];

  const quickActions = [
    { icon: Upload, label: 'Get Verified', link: '/candidate/verification', gradient: 'from-teal-500 to-cyan-500' },
    { icon: Video, label: 'Video Interview', link: '/candidate/video-interview', gradient: 'from-purple-500 to-pink-500' },
    { icon: Briefcase, label: 'Browse Jobs', link: '/candidate/job-matches', gradient: 'from-green-500 to-emerald-500' },
    { icon: Calendar, label: 'My Interviews', link: '/candidate/interviews', gradient: 'from-orange-500 to-amber-500' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50/20 to-cyan-50/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#42b1c2] mx-auto mb-4" />
          <p className="text-gray-600">Loading your enhanced dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50/20 to-cyan-50/20">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header with Verified Badge */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome back, {userName}!</h1>
                <p className="text-gray-600">Here&apos;s your personalized recruitment dashboard</p>
              </div>
              {stats.verifiedStatus && (
                <div className="hidden md:block">
                  <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-3 rounded-full shadow-lg">
                    <ShieldCheck className="h-5 w-5" />
                    <span className="font-semibold">Medically Verified</span>
                    <Star className="h-4 w-4 fill-white" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Profile Summary Card */}
          <Card className="mb-8 border-2 border-teal-200 bg-gradient-to-r from-teal-50 via-cyan-50 to-blue-50 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">Profile Summary</h3>
                  </div>
                  {aiSummary ? (
                    <p className="text-gray-700 leading-relaxed">{aiSummary}</p>
                  ) : (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <div className="animate-spin h-4 w-4 border-2 border-teal-600 border-t-transparent rounded-full"></div>
                      <span>Loading your profile summary...</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Overview with Glowing Accents */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Profile Completion */}
            <Card className="hover:shadow-xl transition-all group border-2 hover:border-teal-300">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Profile Completion</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.profileCompletion}%</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <TrendingUp className="h-6 w-6 text-teal-600" />
                  </div>
                </div>
                <Progress value={stats.profileCompletion} className="h-2" />
                <p className="text-xs text-gray-500 mt-2">Complete your profile to unlock all features</p>
              </CardContent>
            </Card>

            {/* Match Score */}
            <Card className="hover:shadow-xl transition-all group border-2 hover:border-teal-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-teal-500/10 to-cyan-500/10 rounded-bl-full"></div>
              <CardContent className="p-6 relative">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">AI Match Score</p>
                    <p className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">{stats.matchScore}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${
                      i < 4 ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
                    }`} />
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Excellent match potential</p>
              </CardContent>
            </Card>

            {/* Job Matches */}
            <Card className="hover:shadow-xl transition-all group border-2 hover:border-teal-300">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Job Matches</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.jobMatches}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Briefcase className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-700">View opportunities</Badge>
                <p className="text-xs text-gray-500 mt-2">Based on your profile</p>
              </CardContent>
            </Card>

            {/* Interviews */}
            <Card className="hover:shadow-xl transition-all group border-2 hover:border-teal-300">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Scheduled Interviews</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.interviewsScheduled}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Calendar className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
                {stats.interviewsScheduled > 0 ? (
                  <Badge className="bg-purple-100 text-purple-700">Check schedule</Badge>
                ) : (
                  <Badge className="bg-gray-100 text-gray-600">No interviews yet</Badge>
                )}
                <p className="text-xs text-gray-500 mt-2">Stay prepared for opportunities</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Next Steps - Priority Tasks */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Next Steps</h2>
                <Badge className="bg-teal-100 text-teal-700">3 actions</Badge>
              </div>

              {nextSteps.map((step) => {
                const Icon = step.icon;
                return (
                  <Card key={step.id} className="hover:shadow-xl transition-all border-2 hover:border-teal-300 group">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className={`w-14 h-14 ${step.bgColor} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                          <Icon className={`h-7 w-7 ${step.color}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                            {step.priority === 'high' && (
                              <Badge className="bg-red-100 text-red-700 text-xs">High Priority</Badge>
                            )}
                            {step.priority === 'medium' && (
                              <Badge className="bg-orange-100 text-orange-700 text-xs">Medium</Badge>
                            )}
                          </div>
                          <p className="text-gray-600 text-sm mb-4">{step.description}</p>
                          {step.progress > 0 && (
                            <div className="mb-3">
                              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                <span>Progress</span>
                                <span>{step.progress}%</span>
                              </div>
                              <Progress value={step.progress} className="h-2" />
                            </div>
                          )}
                          <Link to={step.action}>
                            <Button className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white group/btn">
                              {step.progress === 100 ? 'Review' : step.progress > 0 ? 'Continue' : 'Start Now'}
                              <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Sidebar - Notifications & Quick Actions */}
            <div className="space-y-6">
              {/* Notifications */}
              <Card className="border-2 border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-lg flex items-center">
                    <Bell className="h-5 w-5 mr-2 text-teal-600" />
                    Notifications
                  </CardTitle>
                  {notifications.filter(n => n.unread).length > 0 && (
                    <Badge className="bg-red-100 text-red-700">{notifications.filter(n => n.unread).length} new</Badge>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {notifications.length > 0 ? (
                    notifications.map((notif) => {
                      const Icon = notif.icon;
                      return (
                        <div 
                          key={notif.id} 
                          className={`p-3 rounded-xl transition-all cursor-pointer ${
                            notif.unread ? 'bg-teal-50 hover:bg-teal-100' : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <Icon className={`h-5 w-5 ${notif.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                              <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                              <p className="text-xs text-gray-500 mt-2">{notif.time}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-4">
                      <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No notifications yet</p>
                    </div>
                  )}
                  <Link to="/candidate/notifications">
                    <Button variant="ghost" size="sm" className="w-full mt-3 text-teal-600 hover:text-teal-700">
                      View All Notifications
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="border-2 border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  {quickActions.map((action, index) => {
                    const Icon = action.icon;
                    return (
                      <Link key={index} to={action.link}>
                        <button className="w-full p-4 rounded-xl bg-white border-2 border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all group">
                          <div className={`w-12 h-12 bg-gradient-to-r ${action.gradient} rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                            <Icon className="h-6 w-6 text-white" />
                          </div>
                          <p className="text-xs font-medium text-gray-900 text-center">{action.label}</p>
                        </button>
                      </Link>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EnhancedDashboard;